import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { ExerciseDefinition, ExerciseType, MovementPattern, MOVEMENT_PATTERNS, MuscleGroup, WorkoutExercise, EXERCISE_NOTES_MAX_LENGTH } from '../../types';
import { apiServices } from '../../api/services';
import { BaseApiService } from '../../api/services/BaseApiService';
import { useAuth } from '../../context/AuthContext';
import { seoConfig } from '../../utils/seoConfig';
import StructuredData from '../seo/StructuredData';
import PageShell from '../layout/PageShell';
import ExerciseSelectionModal from './ExerciseSelectionModal';
import ExerciseDetailModal from './ExerciseDetailModal';
import { useTranslation } from '../../i18n/provider';
import styles from './ExercisesPage.module.css';
import publicExercisesFallback from '../../data/publicExercisesFallback.json';

interface ExercisesPageProps {
  selectedExerciseId?: string | null;
  onClearSelection?: () => void;
  onExerciseAddedToWorkout?: () => void;
  onBackToTemplate?: () => void;
}

const ExercisesPage: React.FC<ExercisesPageProps> = ({ selectedExerciseId, onClearSelection, onExerciseAddedToWorkout, onBackToTemplate }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const routeExerciseId = params.id;
  const effectiveSelectedExerciseId = selectedExerciseId || routeExerciseId;

  const isPublicMode = !isAuthenticated;

  const formatMuscleGroupLabel = (group: string) => {
    const fallback = group.charAt(0).toUpperCase() + group.slice(1);
    return t(`muscleGroups.${group}`, { defaultValue: fallback });
  };

  const formatExerciseTypeLabel = (type: string) => {
    const fallback = type.charAt(0).toUpperCase() + type.slice(1);
    return t(`exerciseTypes.${type}`, { defaultValue: fallback });
  };

  const formatCategoryLabel = (category: string) => {
    const fallback = category.charAt(0).toUpperCase() + category.slice(1);
    return t(`exerciseCategories.${category}`, { defaultValue: fallback });
  };

  const getExerciseDisplayName = (exercise: Pick<ExerciseDefinition, 'name' | 'localizedName'>) => {
    return exercise.localizedName || exercise.name;
  };

  const getExerciseDisplayDescription = (exercise: Pick<ExerciseDefinition, 'description' | 'localizedDescription'>) => {
    return exercise.localizedDescription || exercise.description || '';
  };

  const fallbackExercises = publicExercisesFallback as unknown as ExerciseDefinition[];
  
  // Log component mount once
  useEffect(() => {
    console.log('💪 ExercisesPage mounted');
  }, []);
  
  // Log when selected exercise changes
  useEffect(() => {
    console.log('💪 Selected exercise changed:', effectiveSelectedExerciseId);
  }, [effectiveSelectedExerciseId]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState<MuscleGroup>('all');
  const [selectedType, setSelectedType] = useState<ExerciseType | 'all'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [exercises, setExercises] = useState<ExerciseDefinition[]>([]);
  const [muscleGroups, setMuscleGroups] = useState<MuscleGroup[]>([]);
  const [exerciseTypes, setExerciseTypes] = useState<ExerciseType[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState<ExerciseDefinition | null>(null);
  const [isOpeningSelection, setIsOpeningSelection] = useState(false);
  const [favoriteExerciseIds, setFavoriteExerciseIds] = useState<string[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState<ExerciseDefinition | null>(null);
  const [newExerciseName, setNewExerciseName] = useState('');
  const [newExerciseDetails, setNewExerciseDetails] = useState('');
  const [newExerciseMuscleGroup, setNewExerciseMuscleGroup] = useState<MuscleGroup>('abs');
  const [newExerciseCategories, setNewExerciseCategories] = useState<string[]>([]);
  const [editExerciseName, setEditExerciseName] = useState('');
  const [editExerciseDetails, setEditExerciseDetails] = useState('');
  const [editExerciseMuscleGroup, setEditExerciseMuscleGroup] = useState<MuscleGroup>('abs');
  const [editExerciseEquipment, setEditExerciseEquipment] = useState('');
  // Edit form only (TODO.md B4) - the create form stays minimal by B5's precedent, so there is no
  // newExerciseNotes counterpart.
  const [editExerciseNotes, setEditExerciseNotes] = useState('');
  // '' means unset/not-classified (B5) - a checkbox can't express that third state, so this is a
  // three-way button group like muscleGroup, not a boolean.
  const [editExerciseMovementPattern, setEditExerciseMovementPattern] = useState<MovementPattern | ''>('');
  const [editExerciseCategories, setEditExerciseCategories] = useState<string[]>([]);
  const [editExerciseImageUrl, setEditExerciseImageUrl] = useState('');
  const [editExerciseReferences, setEditExerciseReferences] = useState('');
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [isUploadingEditImage, setIsUploadingEditImage] = useState(false);
  const [editImageUploadError, setEditImageUploadError] = useState<string | null>(null);
  const [privateExerciseIds, setPrivateExerciseIds] = useState<string[]>([]);
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [filterPrivate, setFilterPrivate] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ExerciseDefinition[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(9999);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [maxWeightData, setMaxWeightData] = useState<{ maxWeight: number; unit: string; maxRepsForWeight: number; exerciseName: string; date: string; workoutId: string } | null>(null);

  const selectedExerciseSeoTitle = React.useMemo(() => {
    if (!effectiveSelectedExerciseId || !selectedExercise) return null;
    return `${getExerciseDisplayName(selectedExercise)} | Gym Planner`;
  }, [effectiveSelectedExerciseId, selectedExercise]);

  const selectedExerciseSeoDescription = React.useMemo(() => {
    if (!effectiveSelectedExerciseId || !selectedExercise) return null;
    const raw = getExerciseDisplayDescription(selectedExercise).trim();
    if (raw.length > 0) {
      return raw.length > 160 ? `${raw.slice(0, 157)}...` : raw;
    }
    const muscle = selectedExercise.muscleGroup ? selectedExercise.muscleGroup : 'all';
    return `How to do ${getExerciseDisplayName(selectedExercise)}. Muscle group: ${muscle}. Track workouts and progress with Gym Planner.`;
  }, [effectiveSelectedExerciseId, selectedExercise]);

  const selectedExerciseCanonicalUrl = React.useMemo(() => {
    if (!effectiveSelectedExerciseId) return null;
    return window.location.href;
  }, [effectiveSelectedExerciseId]);

  // Debounced search ref
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sequence guard shared by every flow that does a wholesale replace of `exercises` from a fetch
  // (loadData below, the force-reload effect, and fetchFilteredExercises). Without it, two of these
  // can race and the *slower* one wins regardless of which is actually current - concretely: React
  // 18 StrictMode double-invokes this mount effect in dev, firing loadData() twice; each run's
  // Promise.all does 4 concurrent DB-touching requests against the backend's pool (max 3
  // connections, see gym-planner-backend/AGENTS.md gotcha 7), so under load one of the two duplicate
  // invocations can error and fall into the catch branch below, which resolves to the static public
  // `fallbackExercises` (no private/user-tagged data). If that error branch resolves *after* the
  // user has already clicked a category/type/muscleGroup filter and gotten a correct, narrower
  // result, it silently overwrites that correct state with the fallback - the filter looks "active"
  // and the network call for it clearly succeeded, but the grid renders nothing, because `exercises`
  // itself no longer contains the matching item. Bumping this ref at the start of each flow and only
  // committing state if it's still the latest one closes that race.
  const exercisesRequestSeqRef = useRef(0);

  useEffect(() => {
    // Load exercises and muscle groups when component mounts.
    //
    // TODO.md F2: this used to keep its own 6-hour localStorage cache on top of
    // apiServices.exercises' own in-memory cache - two layers for the same data, and the
    // component's layer was never actually invalidated correctly on writes (deleteExercise() cleared
    // a literal 'exercises_cache' key, but this component's real key was locale-suffixed, e.g.
    // 'exercises_cache:en', so the removeItem silently matched nothing). ExerciseService is now the
    // single source of truth for exercise/muscle-group caching; this effect just calls it directly.
    const loadData = async () => {
      const requestId = ++exercisesRequestSeqRef.current;
      setInitialLoading(true);

      try {
        const [exercisesData, muscleGroupsData, exerciseTypesData, categoriesData] = await Promise.all([
          apiServices.exercises.getExercises(),
          apiServices.exercises.getMuscleGroups(),
          apiServices.exercises.getExerciseTypes(),
          apiServices.exercises.getCategories()
        ]);

        if (exercisesRequestSeqRef.current !== requestId) {
          // A newer request (e.g. a filter click, or - in dev - StrictMode's second invocation of
          // this same effect) has already taken over; applying this stale result would clobber it.
          return;
        }

        const resolvedExercises: ExerciseDefinition[] = (exercisesData && exercisesData.length > 0) ? exercisesData : fallbackExercises;
        const resolvedMuscleGroups = (muscleGroupsData && muscleGroupsData.length > 0)
          ? muscleGroupsData
          : (Array.from(new Set(resolvedExercises.map((e) => e.muscleGroup))) as MuscleGroup[]);

        setExercises(resolvedExercises);
        setMuscleGroups(resolvedMuscleGroups);
        setExerciseTypes(exerciseTypesData || []);
        setCategories(categoriesData || []);

      } catch (error) {
        console.error('Failed to load exercises:', error);
        if (exercisesRequestSeqRef.current !== requestId) {
          return;
        }
        const resolvedExercises = fallbackExercises;
        const resolvedMuscleGroups = (Array.from(new Set(resolvedExercises.map((e) => e.muscleGroup))) as MuscleGroup[]);
        setExercises(resolvedExercises);
        setMuscleGroups(resolvedMuscleGroups);
      } finally {
        if (exercisesRequestSeqRef.current === requestId) {
          setInitialLoading(false);
        }
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    // After deleting an exercise we navigate back to /exercises, but this page can remain mounted.
    // If so, it may keep showing stale in-memory state. Honor a one-time reload flag.
    if (!effectiveSelectedExerciseId) {
      let forceReload = false;
      try {
        forceReload = sessionStorage.getItem('exercises_force_reload') === '1';
      } catch {
        forceReload = false;
      }

      if (forceReload) {
        try {
          sessionStorage.removeItem('exercises_force_reload');
        } catch {
          // ignore storage errors
        }

        void (async () => {
          const requestId = ++exercisesRequestSeqRef.current;
          try {
            setInitialLoading(true);
            const [freshExercises, freshMuscleGroups] = await Promise.all([
              apiServices.exercises.getExercises(),
              apiServices.exercises.getMuscleGroups(),
            ]);
            if (exercisesRequestSeqRef.current !== requestId) {
              return;
            }
            setExercises(freshExercises);
            setMuscleGroups(freshMuscleGroups);
          } catch (error) {
            console.error('Failed to force reload exercises:', error);
          } finally {
            if (exercisesRequestSeqRef.current === requestId) {
              setInitialLoading(false);
            }
          }
        })();
      }
    }
  }, [effectiveSelectedExerciseId]);

  useEffect(() => {
    if (!effectiveSelectedExerciseId) {
      return;
    }

    let cancelled = false;

    const loadExerciseById = async () => {
      setIsOpeningSelection(true);
      try {
        const exercise = await apiServices.exercises.getExerciseById(effectiveSelectedExerciseId);
        if (!cancelled && exercise) {
          setSelectedExercise(exercise);
        }
      } catch (error) {
        console.error('Failed to load exercise by id:', effectiveSelectedExerciseId, error);
        if (!cancelled) {
          setSelectedExercise(null);
        }
      } finally {
        if (!cancelled) {
          setIsOpeningSelection(false);
        }
      }
    };

    loadExerciseById();

    return () => {
      cancelled = true;
    };
  }, [effectiveSelectedExerciseId]);

  // Load max weight data when selected exercise changes
  useEffect(() => {
    if (selectedExercise && user?.id && !isPublicMode) {
      console.log('Fetching max weight for:', selectedExercise.name, 'user:', user.id);
      apiServices.workoutHistory.getMaxWeightForExercise(user.id, selectedExercise.name)
        .then(data => {
          console.log('Max weight data received:', data);
          setMaxWeightData(data);
        })
        .catch(error => {
          console.error('Error fetching max weight:', error);
          setMaxWeightData(null);
        });
    } else {
      console.log('No selected exercise or user, clearing max weight data');
      setMaxWeightData(null);
    }
  }, [selectedExercise, user, isPublicMode]);

  // Debounced search effect
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const performSearch = async () => {
      if (searchQuery.trim() === '') {
        // Empty search - use regular exercises
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        console.log(`🔍 Performing debounced search for: "${searchQuery}"`);
        const results = await apiServices.exercises.searchExercises(searchQuery);
        setSearchResults(results);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Set new timeout for debounced search (500ms delay)
    if (searchQuery.trim() !== '') {
      searchTimeoutRef.current = setTimeout(performSearch, 500);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Client-side safety net for muscleGroup/type/category: the backend already applies these filters
  // (see fetchFilteredExercises below), but `exercises` can be repopulated by flows that don't know
  // about the current selection (initial load, the force-reload effect, image upload refresh), so this
  // re-applies them locally to keep the UI consistent regardless of how `exercises` got set.
  const matchesActiveFilters = (ex: ExerciseDefinition) =>
    (selectedMuscleGroup === 'all' || ex.muscleGroup === selectedMuscleGroup) &&
    (selectedType === 'all' || ex.exerciseType === selectedType) &&
    (selectedCategory === 'all' || (ex.categories || []).includes(selectedCategory));

  const filteredExercises = exercises.filter(matchesActiveFilters);

  // Use backend search results if searching, otherwise use filtered exercises
  const searchFilteredExercises = searchQuery.trim()
    ? searchResults.filter(matchesActiveFilters)
    : filteredExercises;

  // Apply Favorites/Private filters (AND logic when both on)
  const fullyFilteredExercises = (
    filterPrivate
      ? searchFilteredExercises.filter(ex => ex.isPrivate === true || privateExerciseIds.includes(ex.id))
      : searchFilteredExercises
  );
  const finalExercises = (
    filterFavorites
      ? fullyFilteredExercises.filter(ex => favoriteExerciseIds.includes(ex.id))
      : fullyFilteredExercises
  );

  // Paginated exercises - show only displayedCount items
  const paginatedExercises = finalExercises.slice(0, displayedCount);
  const hasMore = displayedCount < finalExercises.length;

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Re-fetch from the backend with the composed muscleGroup/type/category filters (TODO.md E#3).
  // Each of handleMuscleGroupSelect/handleTypeSelect/handleCategorySelect calls this with just the
  // one value it owns changing; the other two come from current state, so selecting a muscle group
  // does not silently drop an already-active type or category filter (and vice versa).
  const fetchFilteredExercises = async (overrides: {
    muscleGroup?: MuscleGroup;
    type?: ExerciseType | 'all';
    category?: string;
  }) => {
    const muscleGroup = overrides.muscleGroup ?? selectedMuscleGroup;
    const type = overrides.type ?? selectedType;
    const category = overrides.category ?? selectedCategory;

    const requestId = ++exercisesRequestSeqRef.current;
    setInitialLoading(true);
    try {
      const filters: { muscleGroup?: MuscleGroup; type?: ExerciseType; category?: string } = {};
      if (muscleGroup !== 'all') filters.muscleGroup = muscleGroup;
      if (type !== 'all') filters.type = type;
      if (category !== 'all') filters.category = category;

      const exercisesData = await apiServices.exercises.getExercises(
        Object.keys(filters).length > 0 ? filters : undefined
      );
      if (exercisesRequestSeqRef.current !== requestId) {
        // A newer request (e.g. the initial mount load's duplicate StrictMode invocation, or
        // another filter click) has already taken over - see exercisesRequestSeqRef above.
        return;
      }
      setExercises(exercisesData);
    } catch (error) {
      console.error('Failed to fetch filtered exercises:', error);
    } finally {
      if (exercisesRequestSeqRef.current === requestId) {
        setInitialLoading(false);
      }
    }
  };

  const handleMuscleGroupSelect = (group: MuscleGroup) => {
    setSelectedMuscleGroup(group);
    void fetchFilteredExercises({ muscleGroup: group });
  };

  const handleTypeSelect = (type: ExerciseType | 'all') => {
    setSelectedType(type);
    void fetchFilteredExercises({ type });
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    void fetchFilteredExercises({ category });
  };

  // Reset pagination when filters change
  useEffect(() => {
    setDisplayedCount(9999);
  }, [searchQuery, selectedMuscleGroup, selectedType, selectedCategory, filterFavorites, filterPrivate]);

  // Infinite scroll handler
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    
    // Load more when user is within 200px of bottom
    if (scrollBottom < 200 && hasMore && !isLoadingMore) {
      setIsLoadingMore(true);
      
      // Simulate loading delay (remove in production or adjust as needed)
      setTimeout(() => {
        setDisplayedCount(prev => prev + 20);
        setIsLoadingMore(false);
      }, 500);
    }
  };

  const handleAddExercise = () => {
    if (isPublicMode) {
      return;
    }
    // Reuse FAB to open the Create Exercise form instead of the selection modal
    setSelectedExercise(null);
    setNewExerciseMuscleGroup(selectedMuscleGroup !== 'all' ? selectedMuscleGroup : 'abs');
    setShowCreateForm(true);
  };

  const handleSelectExercise = (exercise: ExerciseDefinition) => {
    console.log('Selected exercise:', exercise);
    // Here you would typically add the exercise to the current workout
    // For now, we'll just close the modal
    setShowExerciseModal(false);
  };

  const handleExerciseClick = (exercise: ExerciseDefinition) => {
    navigate(`/exercises/${exercise.id}`);
  };

  const handleCloseDetail = () => {
    setSelectedExercise(null);
    if (onClearSelection) {
      onClearSelection();
    }

    if (onBackToTemplate) {
      onBackToTemplate();
      return;
    }

    navigate('/exercises');
  };

  const handleAddToWorkout = async (exercise: ExerciseDefinition) => {
    // Load current session from backend
    const currentExercises = await apiServices.workoutSession.getExercises();
    
    // Prevent duplicates
    if (currentExercises.some((ex: WorkoutExercise) => ex.id === exercise.id)) {
      alert(t('exercises.alerts.duplicateInWorkout'));
      return;
    }
    
    // Get max weight from workout history using API method
    const maxWeightData = user?.id 
      ? await apiServices.workoutHistory.getMaxWeightForExercise(user.id, exercise.name)
      : { maxWeight: 0, unit: 'kg', maxRepsForWeight: 0, exerciseName: exercise.name, date: new Date().toISOString(), workoutId: 'fallback' };
    
    // Create new workout exercise with pre-filled data from history
    const newExercise: any = {
      id: exercise.id,
      name: getExerciseDisplayName(exercise),
      localizedName: exercise.localizedName,
      muscleGroup: exercise.muscleGroup,
      imageUrl: exercise.imageUrl || '',
      description: exercise.description,
      localizedDescription: exercise.localizedDescription,
      sets: 1,
      completedSets: 0,
      reps: maxWeightData.maxRepsForWeight || 1, // Fallback to 1 if undefined
      weight: Number(maxWeightData.maxWeight) || 0, // Ensure always a number, fallback to 0
      unit: maxWeightData.unit || 'kg', // Fallback to kg if undefined
      completed: false,
      inProgress: false,
    };
    
    console.log('🏋️ [EXERCISES PAGE] Adding exercise to workout:', {
      name: newExercise.name,
      weight: newExercise.weight,
      weightType: typeof newExercise.weight,
      maxWeightData
    });
    
    // Add to workout
    await apiServices.workoutSession.setExercises([...currentExercises, newExercise]);
    
    // Navigate back to workout
    if (onExerciseAddedToWorkout) {
      onExerciseAddedToWorkout();
    }
  };

  const handleToggleFavorite = (exercise: ExerciseDefinition) => {
    if (isPublicMode) {
      alert(t('exercises.alerts.loginForFavorites'));
      return;
    }
    setFavoriteExerciseIds(prev =>
      prev.includes(exercise.id)
        ? prev.filter(id => id !== exercise.id)
        : [...prev, exercise.id]
    );
  };

  const closeCreateForm = () => {
    setShowCreateForm(false);
    // keep user inputs for now, or clear if preferred
  };

  const closeEditForm = () => {
    setShowEditForm(false);
    setEditingExercise(null);
    setEditImageFile(null);
    setIsUploadingEditImage(false);
    setEditImageUploadError(null);
  };

  const handleEditExercise = (exercise: ExerciseDefinition) => {
    if (isPublicMode) {
      alert(t('exercises.alerts.loginForEdit'));
      return;
    }
    setShowCreateForm(false);
    setSelectedExercise(null);

    setEditingExercise(exercise);
    setEditExerciseName(exercise.name || '');
    setEditExerciseDetails(exercise.description || '');
    // Edits the canonical `notes` field only, same as editExerciseDetails above only reads
    // `exercise.description` (not localizedDescription) - localizedNotes is a server-managed i18n
    // catalog override, not something this raw-text form edits directly.
    setEditExerciseNotes(exercise.notes || '');
    setEditExerciseMuscleGroup((exercise.muscleGroup as MuscleGroup) || 'abs');
    setEditExerciseEquipment(exercise.equipment || '');
    setEditExerciseMovementPattern(exercise.movementPattern || '');
    setEditExerciseCategories(exercise.categories || []);
    setEditExerciseImageUrl(exercise.imageUrl || '');
    setEditExerciseReferences((exercise.references || []).join('\n'));

    setEditImageFile(null);
    setIsUploadingEditImage(false);
    setEditImageUploadError(null);

    setShowEditForm(true);
  };

  const uploadEditExerciseImage = async () => {
    if (isPublicMode) {
      alert(t('exercises.alerts.loginForImageUpload'));
      return;
    }
    if (!editingExercise || !editImageFile) {
      return;
    }

    setIsUploadingEditImage(true);
    setEditImageUploadError(null);
    try {
      const updated = await apiServices.exercises.uploadExerciseImage(editingExercise.id, editImageFile);

      const baseImageUrl = updated.imageUrl || '';
      const cacheBustedImageUrl = baseImageUrl
        ? `${baseImageUrl}${baseImageUrl.includes('?') ? '&' : '?'}v=${Date.now()}`
        : '';

      const updatedWithCacheBustedUrl: ExerciseDefinition = {
        ...updated,
        imageUrl: cacheBustedImageUrl || updated.imageUrl,
      };

      setEditExerciseImageUrl(cacheBustedImageUrl);
      setEditingExercise(updatedWithCacheBustedUrl);
      setSelectedExercise(updatedWithCacheBustedUrl);

      const exercisesData = await apiServices.exercises.getExercises();
      setExercises(
        exercisesData.map((ex) =>
          ex.id === updatedWithCacheBustedUrl.id
            ? { ...ex, imageUrl: updatedWithCacheBustedUrl.imageUrl }
            : ex
        )
      );

      setEditImageFile(null);
    } catch (e: any) {
      setEditImageUploadError(e?.message || t('exercises.alerts.uploadFailed'));
    } finally {
      setIsUploadingEditImage(false);
    }
  };

  const saveEditExercise = async () => {
    if (isPublicMode) {
      alert(t('exercises.alerts.loginForEdit'));
      return;
    }
    if (!editingExercise) {
      return;
    }

    if (!editExerciseName.trim()) {
      console.warn('Exercise name is required');
      return;
    }

    const updated = await apiServices.exercises.updateExercise(editingExercise.id, {
      name: editExerciseName.trim(),
      muscleGroup: editExerciseMuscleGroup,
      description: editExerciseDetails || undefined,
      // '' (cleared) is omitted rather than sent - same COALESCE-preserves-on-omit gap as description
      // above (ADR-006's B5.4). The 4000-char cap is enforced server-side too; the form's own
      // maxLength/counter (below) is a UX affordance, not the real control.
      notes: editExerciseNotes || undefined,
      equipment: editExerciseEquipment || undefined,
      // '' (unset) is omitted rather than sent - the backend can't distinguish an explicit clear from
      // "leave unchanged" (COALESCE), so sending '' would 400 against the compound/isolation CHECK
      // instead of clearing it. Same accepted gap as every other optional exercise field (B5.4).
      movementPattern: editExerciseMovementPattern || undefined,
      // Always sent, including empty - this multi-select shows the exercise's full current tag set,
      // so unchecking everything is a deliberate "clear all tags" (the backend replaces the full set
      // when categories is provided, unlike the COALESCE fields above).
      categories: editExerciseCategories,
      imageUrl: editExerciseImageUrl || undefined,
      references: editExerciseReferences.trim() ? editExerciseReferences.trim().split('\n').filter(ref => ref.trim()) : undefined,
    });

    const exercisesData = await apiServices.exercises.getExercises();
    setExercises(exercisesData);

    if (updated.isPrivate) {
      setPrivateExerciseIds((prev: string[]) => (prev.includes(updated.id) ? prev : [...prev, updated.id]));
    } else {
      setPrivateExerciseIds((prev: string[]) => prev.filter(id => id !== updated.id));
    }

    setShowEditForm(false);
    setEditingExercise(null);
    setSelectedExercise(updated);
  };

  const saveCreateExercise = async () => {
    if (isPublicMode) {
      alert(t('exercises.alerts.loginForCreate'));
      return;
    }
    if (!newExerciseName.trim()) {
      // Basic guard; we could add UI validation later
      console.warn('Exercise name is required');
      return;
    }

    const group: MuscleGroup = newExerciseMuscleGroup;

    // Create exercise in backend - always private, enforced by backend
    const created = await apiServices.exercises.createExercise({
      name: newExerciseName.trim(),
      muscleGroup: group,
      imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/65/No-Image-Placeholder.svg/1665px-No-Image-Placeholder.svg.png',
      description: newExerciseDetails || undefined,
      categories: newExerciseCategories.length > 0 ? newExerciseCategories : undefined,
      userId: user?.id,
      isPrivate: true, // Always private for new exercises
    });

    // Refresh local list
    const exercisesData = await apiServices.exercises.getExercises();
    setExercises(exercisesData);

    // Track privacy locally (always true for new exercises)
    setPrivateExerciseIds((prev: string[]) => [...prev, created.id]);

    // Optionally, reset fields
    setNewExerciseName('');
    setNewExerciseDetails('');
    setNewExerciseMuscleGroup(selectedMuscleGroup !== 'all' ? selectedMuscleGroup : 'abs');
    setNewExerciseCategories([]);

    // Close form and return to grid (could also open detail of created)
    setShowCreateForm(false);
    setSelectedExercise(null);

    console.log('Created exercise:', created, {
      private: true, // Always private
      details: newExerciseDetails,
    });
  };

  return (
    <>
      <StructuredData type="WebApplication" name="Exercise Creator - Free Weightlifting Planner" />
      <Helmet>
        <title>{selectedExerciseSeoTitle || seoConfig.exercises.title}</title>
        <meta name="description" content={selectedExerciseSeoDescription || seoConfig.exercises.description} />
        {seoConfig.exercises.keywords && <meta name="keywords" content={seoConfig.exercises.keywords} />}
        {selectedExerciseCanonicalUrl && <link rel="canonical" href={selectedExerciseCanonicalUrl} />}
        <meta property="og:title" content={selectedExerciseSeoTitle || seoConfig.exercises.title} />
        <meta property="og:description" content={selectedExerciseSeoDescription || seoConfig.exercises.description} />
        <meta property="og:type" content={effectiveSelectedExerciseId ? 'article' : 'website'} />
        {effectiveSelectedExerciseId && selectedExercise && selectedExercise.imageUrl && (
          <meta property="og:image" content={selectedExercise.imageUrl} />
        )}
        <meta name="twitter:title" content={selectedExerciseSeoTitle || seoConfig.exercises.title} />
        <meta name="twitter:description" content={selectedExerciseSeoDescription || seoConfig.exercises.description} />
        <meta name="twitter:card" content={effectiveSelectedExerciseId ? 'summary_large_image' : 'summary'} />
        {effectiveSelectedExerciseId && selectedExercise && selectedExercise.imageUrl && (
          <meta name="twitter:image" content={selectedExercise.imageUrl} />
        )}
      </Helmet>

      <PageShell variant="page">
        <div className={styles.exercisesPage}>
          {initialLoading || isOpeningSelection ? (
            <div className={styles.fullPageLoadingContainer}>
              <div className={styles.fullPageLoadingSpinner}></div>
              <p className={styles.fullPageLoadingText}>
                {isOpeningSelection ? t('exercises.loading.opening') : t('exercises.loading.page')}
              </p>
            </div>
          ) : (
            <>
              {(!showCreateForm && !showEditForm && !selectedExercise) && (
                <>
                  <h1 className="content-title" style={{ margin: '0 0 12px 0' }}>{t('exercises.title')}</h1>
                  <div className={styles.searchContainer}>
                    <div className={styles.searchInputWrapper}>
                      <input
                        type="text"
                        placeholder={isSearching ? t('exercises.search.searching') : t('exercises.search.placeholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={`${styles.searchInput} ${isSearching ? styles.searching : ''}`}
                        disabled={isSearching}
                      />
                      {isSearching && (
                        <div className={styles.searchSpinner} aria-label={t('exercises.search.searching')}>
                          <div className={styles.spinner}></div>
                        </div>
                      )}
                    </div>
                    {searchQuery && !isSearching && (
                      <button
                        className={styles.clearSearch}
                        onClick={() => setSearchQuery('')}
                        aria-label={t('exercises.search.clear')}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className={styles.filterHeader} onClick={toggleFilters}>
                    <div className={styles.filterTitle}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <button
                          data-testid="muscle-group-filter-all"
                          className={`${styles.filterOption} ${selectedMuscleGroup === 'all' ? styles.active : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMuscleGroupSelect('all');
                          }}
                        >
                          {t('exercises.filters.all')}
                        </button>
                        {muscleGroups.map(group => (
                          <button
                            key={group}
                            data-testid={`muscle-group-filter-${group}`}
                            className={`${styles.filterOption} ${selectedMuscleGroup === group ? styles.active : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMuscleGroupSelect(group);
                            }}
                          >
                            {formatMuscleGroupLabel(group)}
                          </button>
                        ))}
                        <label
                          className={`${styles.filterCheckboxLabel} ${isPublicMode ? styles.filterCheckboxLabelDisabled : ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className={styles.filterCheckbox}
                            checked={filterFavorites}
                            onChange={(e) => setFilterFavorites(e.target.checked)}
                            disabled={isPublicMode}
                          />
                          <span>{t('exercises.filters.favorites')}</span>
                        </label>
                        <label
                          className={`${styles.filterCheckboxLabel} ${isPublicMode ? styles.filterCheckboxLabelDisabled : ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className={styles.filterCheckbox}
                            checked={filterPrivate}
                            onChange={(e) => setFilterPrivate(e.target.checked)}
                            disabled={isPublicMode}
                          />
                          <span>{t('exercises.filters.private')}</span>
                        </label>
                      </div>

                      {exerciseTypes.length > 0 && (
                        <div className={styles.filterOptions}>
                          <span style={{ opacity: 0.9, color: '#e0a7a7', fontSize: '0.85rem' }}>
                            {t('exercises.filters.typeLabel')}:
                          </span>
                          <button
                            data-testid="type-filter-all"
                            className={`${styles.filterOption} ${selectedType === 'all' ? styles.active : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTypeSelect('all');
                            }}
                          >
                            {t('exercises.filters.all')}
                          </button>
                          {exerciseTypes.map(type => (
                            <button
                              key={type}
                              data-testid={`type-filter-${type}`}
                              className={`${styles.filterOption} ${selectedType === type ? styles.active : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTypeSelect(type);
                              }}
                            >
                              {formatExerciseTypeLabel(type)}
                            </button>
                          ))}
                        </div>
                      )}

                      {categories.length > 0 && (
                        <div className={styles.filterOptions}>
                          <span style={{ opacity: 0.9, color: '#e0a7a7', fontSize: '0.85rem' }}>
                            {t('exercises.filters.categoryLabel')}:
                          </span>
                          <button
                            data-testid="category-filter-all"
                            className={`${styles.filterOption} ${selectedCategory === 'all' ? styles.active : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCategorySelect('all');
                            }}
                          >
                            {t('exercises.filters.all')}
                          </button>
                          {categories.map(category => (
                            <button
                              key={category}
                              data-testid={`category-filter-${category}`}
                              className={`${styles.filterOption} ${selectedCategory === category ? styles.active : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCategorySelect(category);
                              }}
                            >
                              {formatCategoryLabel(category)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

        {showCreateForm ? (
          <div className="modal-overlay" onClick={closeCreateForm}>
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={styles.closeButton}
                onClick={closeCreateForm}
                aria-label="Close"
              >
                ✕
              </button>
              <div className={styles.createCard}>
                <div className={styles.createHeader}>
                  <div className={styles.createTitle}>{t('exercises.form.createTitle')}</div>
                </div>

                <div className={styles.formColumn}>
                  <label className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.name')}</span>
                    <input
                      type="text"
                      value={newExerciseName}
                      onChange={(e) => setNewExerciseName(e.target.value)}
                      placeholder={t('exercises.form.namePlaceholder')}
                      className={styles.textInput}
                    />
                  </label>

                  <div className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.muscleGroup')}</span>
                    <div className={styles.muscleGroupOptions} style={{ marginTop: 8 }}>
                      {(muscleGroups.length > 0 ? muscleGroups : (['abs'] as MuscleGroup[]))
                        .filter((mg) => mg !== 'all')
                        .map((mg) => (
                          <button
                            key={mg}
                            type="button"
                            className={`${styles.muscleGroupOption} ${newExerciseMuscleGroup === mg ? styles.active : ''}`}
                            onClick={() => setNewExerciseMuscleGroup(mg)}
                          >
                            {formatMuscleGroupLabel(mg)}
                          </button>
                        ))}
                    </div>
                  </div>

                  {categories.length > 0 && (
                    <div className={styles.labelColumn}>
                      <span style={{ opacity: 0.9 }}>{t('exercises.form.categories')}</span>
                      <div className={styles.muscleGroupOptions} style={{ marginTop: 8 }}>
                        {categories.map((category) => (
                          <button
                            key={category}
                            type="button"
                            data-testid={`category-toggle-${category}`}
                            className={`${styles.muscleGroupOption} ${newExerciseCategories.includes(category) ? styles.active : ''}`}
                            onClick={() => setNewExerciseCategories((prev) =>
                              prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
                            )}
                          >
                            {formatCategoryLabel(category)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <label className={styles.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={true}
                      disabled={true}
                      style={{ cursor: 'not-allowed', opacity: 0.6 }}
                    />
                    <span>{t('exercises.form.privateHint')}</span>
                  </label>

                  <label className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.details')}</span>
                    <textarea
                      value={newExerciseDetails}
                      onChange={(e) => setNewExerciseDetails(e.target.value)}
                      placeholder={t('exercises.form.detailsPlaceholder')}
                      rows={6}
                      className={styles.textArea}
                    />
                  </label>

                  <label className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.pictureAfterSave')}</span>
                  </label>
                </div>

                <div className={styles.cardActions}>
                  <button onClick={closeCreateForm} className={`${styles.btn} ${styles.btnSecondary}`}>
                    {t('exercises.form.close')}
                  </button>
                  <button onClick={saveCreateExercise} className={`${styles.btn} ${styles.btnPrimary}`}>
                    {t('exercises.form.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : showEditForm ? (
          <div className="modal-overlay" onClick={closeEditForm}>
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{ maxHeight: '85vh', overflow: 'auto' }}
            >
              <button
                className={styles.closeButton}
                onClick={closeEditForm}
                aria-label="Close"
              >
                ✕
              </button>
              <div className={styles.createCard}>
                <div className={styles.createHeader}>
                  <div className={styles.createTitle}>{t('exercises.form.editTitle')}</div>
                </div>

                <div className={styles.formColumn}>
                  <label className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.name')}</span>
                    <input
                      type="text"
                      value={editExerciseName}
                      onChange={(e) => setEditExerciseName(e.target.value)}
                      placeholder={t('exercises.form.namePlaceholder')}
                      className={styles.textInput}
                    />
                  </label>

                  <div className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.muscleGroup')}</span>
                    <div className={styles.muscleGroupOptions} style={{ marginTop: 8 }}>
                      {(muscleGroups.length > 0 ? muscleGroups : (['abs'] as MuscleGroup[]))
                        .filter((mg) => mg !== 'all')
                        .map((mg) => (
                          <button
                            key={mg}
                            type="button"
                            className={`${styles.muscleGroupOption} ${editExerciseMuscleGroup === mg ? styles.active : ''}`}
                            onClick={() => setEditExerciseMuscleGroup(mg)}
                          >
                            {formatMuscleGroupLabel(mg)}
                          </button>
                        ))}
                    </div>
                  </div>

                  <div className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.movementPattern')}</span>
                    <div className={styles.muscleGroupOptions} style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className={`${styles.muscleGroupOption} ${editExerciseMovementPattern === '' ? styles.active : ''}`}
                        onClick={() => setEditExerciseMovementPattern('')}
                      >
                        {t('exercises.form.movementPatternUnset')}
                      </button>
                      {MOVEMENT_PATTERNS.map((pattern) => (
                        <button
                          key={pattern}
                          type="button"
                          className={`${styles.muscleGroupOption} ${editExerciseMovementPattern === pattern ? styles.active : ''}`}
                          onClick={() => setEditExerciseMovementPattern(pattern)}
                        >
                          {t(`exercises.form.movementPattern${pattern === 'compound' ? 'Compound' : 'Isolation'}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {categories.length > 0 && (
                    <div className={styles.labelColumn}>
                      <span style={{ opacity: 0.9 }}>{t('exercises.form.categories')}</span>
                      <div className={styles.muscleGroupOptions} style={{ marginTop: 8 }}>
                        {categories.map((category) => (
                          <button
                            key={category}
                            type="button"
                            className={`${styles.muscleGroupOption} ${editExerciseCategories.includes(category) ? styles.active : ''}`}
                            onClick={() => setEditExerciseCategories((prev) =>
                              prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
                            )}
                          >
                            {formatCategoryLabel(category)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <label className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.equipment')}</span>
                    <input
                      type="text"
                      value={editExerciseEquipment}
                      onChange={(e) => setEditExerciseEquipment(e.target.value)}
                      placeholder={t('exercises.form.equipmentPlaceholder')}
                      className={styles.textInput}
                    />
                  </label>

                  <label className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.imageUrl')}</span>
                    <input
                      type="text"
                      value={editExerciseImageUrl}
                      onChange={(e) => setEditExerciseImageUrl(e.target.value)}
                      placeholder="https://..."
                      className={styles.textInput}
                    />
                  </label>

                  <label className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.videoReferences')}</span>
                    <textarea
                      value={editExerciseReferences}
                      onChange={(e) => setEditExerciseReferences(e.target.value)}
                      placeholder={t('exercises.form.videoReferencesPlaceholder')}
                      rows={4}
                      className={styles.textArea}
                    />
                  </label>

                  <div className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.exercisePicture')}</span>
                    {(editingExercise?.imageUrl || editExerciseImageUrl) ? (
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <img
                          src={BaseApiService.resolveImageUrl(editingExercise?.imageUrl || editExerciseImageUrl) as string}
                          alt="Exercise"
                          style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 10, background: '#111' }}
                        />
                        <div style={{ opacity: 0.8, fontSize: 13, lineHeight: 1.4 }}>
                          {t('exercises.form.uploadReplaceHint')}
                        </div>
                      </div>
                    ) : (
                      <div style={{ opacity: 0.8, fontSize: 13, lineHeight: 1.4 }}>
                        {t('exercises.form.noPicture')}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setEditImageFile(e.target.files?.[0] || null)}
                        disabled={isUploadingEditImage}
                      />
                      <button
                        type="button"
                        onClick={uploadEditExerciseImage}
                        disabled={!editImageFile || isUploadingEditImage}
                        className={`${styles.btn} ${styles.btnPrimary}`}
                      >
                        {isUploadingEditImage ? t('exercises.form.uploading') : t('exercises.form.upload')}
                      </button>
                    </div>
                    {editImageUploadError && (
                      <div style={{ color: '#ff8a8a', fontSize: 13, marginTop: 6 }}>
                        {editImageUploadError}
                      </div>
                    )}
                  </div>

                  <label className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.details')}</span>
                    <textarea
                      value={editExerciseDetails}
                      onChange={(e) => setEditExerciseDetails(e.target.value)}
                      placeholder={t('exercises.form.detailsPlaceholder')}
                      rows={6}
                      className={styles.textArea}
                    />
                  </label>

                  {/* Edit form only (TODO.md B4) - the create form stays minimal by B5's precedent. */}
                  <label className={styles.labelColumn}>
                    <span style={{ opacity: 0.9 }}>{t('exercises.form.notes')}</span>
                    <textarea
                      value={editExerciseNotes}
                      onChange={(e) => setEditExerciseNotes(e.target.value.slice(0, EXERCISE_NOTES_MAX_LENGTH))}
                      placeholder={t('exercises.form.notesPlaceholder')}
                      maxLength={EXERCISE_NOTES_MAX_LENGTH}
                      rows={6}
                      className={styles.textArea}
                      data-testid="exercise-edit-notes-input"
                    />
                    <div style={{ opacity: 0.6, fontSize: 12, marginTop: 4 }} data-testid="exercise-edit-notes-remaining">
                      {t('exercises.form.notesCharactersRemaining', { count: EXERCISE_NOTES_MAX_LENGTH - editExerciseNotes.length })}
                    </div>
                  </label>
                </div>

                <div className={styles.cardActions}>
                  <button onClick={closeEditForm} className={`${styles.btn} ${styles.btnSecondary}`}>
                    {t('exercises.form.close')}
                  </button>
                  <button onClick={saveEditExercise} className={`${styles.btn} ${styles.btnPrimary}`}>
                    {t('exercises.form.save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <ExerciseDetailModal
              exercise={selectedExercise}
              onClose={handleCloseDetail}
              onAddToWorkout={handleAddToWorkout}
              onToggleFavorite={handleToggleFavorite}
              onEditExercise={handleEditExercise}
              isFavorite={selectedExercise ? favoriteExerciseIds.includes(selectedExercise.id) : false}
              maxWeightData={maxWeightData}
              onBackToTemplate={onBackToTemplate}
            />
            {!selectedExercise && (
              <div className={styles.exercisesGrid} onScroll={handleScroll}>
                {paginatedExercises.map(exercise => (
                  <div key={exercise.id} className={styles.gridItem} data-testid="exercise-item" onClick={() => handleExerciseClick(exercise)}>
                    <div className={styles.gridImage}>
                      <img
                        src={BaseApiService.resolveImageUrl(exercise.imageUrl)}
                        alt={`${getExerciseDisplayName(exercise)} preview`}
                        loading="lazy"
                      />
                    </div>
                    <div className={styles.gridItemText}>{getExerciseDisplayName(exercise)}</div>
                  </div>
                ))}
                
                {isLoadingMore && (
                  <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}></div>
                    <p className={styles.loadingText}>{t('exercises.loading.more')}</p>
                  </div>
                )}
                
                {!hasMore && paginatedExercises.length > 0 && (
                  <div className={styles.endMessage}>
                    <p>{t('exercises.endReached', { count: paginatedExercises.length })}</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {(!showCreateForm && !showEditForm && !selectedExercise) && !isPublicMode && (
          <button className="add-exercise-fab" onClick={handleAddExercise}>
            <span className="fab-icon">+</span>
          </button>
        )}

        {showExerciseModal && (
          <ExerciseSelectionModal
            isOpen={showExerciseModal}
            onClose={() => setShowExerciseModal(false)}
            onSelectExercise={handleSelectExercise}
            exercises={exercises}
            selectedMuscleGroup={selectedMuscleGroup}
            onMuscleGroupChange={setSelectedMuscleGroup}
            muscleGroups={muscleGroups}
          />
        )}
            </>
          )}
        </div>
      </PageShell>
    </>
  );
};

export default ExercisesPage;
